import { useMemo } from 'react';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxMutePolicy } from '@frontend/inbox-mute-policy';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore, useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { HiddenFilterKeys, InboxArrayFilters, InboxFilter } from '../types';

type UseHasVisibleFilterOptionsArgs = {
  hideFilters?: HiddenFilterKeys;
  groupIds?: string[];
};

/**
 * Determines whether the inbox filters button has any visible filter options to show.
 * Returns false when all filters are hidden (by mute policy, feature flags, or phone count),
 * which signals the filter button itself should be hidden.
 */
export const useHasVisibleFilterOptions = ({ hideFilters, groupIds }: UseHasVisibleFilterOptionsArgs): boolean => {
  const { isHidden } = useInboxMutePolicy();
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds } = useAppScopeStore();
  const locationIds = groupIds ?? selectedLocationIds;
  const { isActive: hasAIEnabled } = useMessagingAIRCustomizationFlag(locationIds);
  const {
    data: { allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();

  const hasInboxTasksEnabled = getFeatureFlagValue({
    locationIds,
    flagName: 'nwx:inbox-tasks',
    strategy: 'ALL',
  });

  return useMemo(() => {
    const isFilterHidden = (key: InboxFilter | InboxArrayFilters | 'date' | 'time') =>
      !!hideFilters?.includes(key as InboxFilter);

    const hasDateOrTime = !isFilterHidden('date') || !isFilterHidden('time');

    const hasArrayFilters =
      (!isFilterHidden('tags') && !isHidden('filter-tags')) ||
      (!isFilterHidden('locationPhones') && allLocationPhones.length >= 2) ||
      (!isFilterHidden('tasks') && hasInboxTasksEnabled && !isHidden('filter-tasks')) ||
      (!isFilterHidden('aiReceptionist') && hasAIEnabled && !isHidden('filter-ai-receptionist'));

    return hasDateOrTime || hasArrayFilters;
  }, [hideFilters, isHidden, allLocationPhones.length, hasInboxTasksEnabled, hasAIEnabled]);
};
